import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { Provider } from "@redotech/redo-order-common/provider";
import { groupInput, input, InputSchema, listInput } from "@redotech/ui/form";

/**
 * Providers that require both a tracking number and carrier before the
 * marketplace can be notified, or the backend fulfillment call will fail.
 */
export const ProvidersRequiringTrackingForFulfillment = [
  Provider.WALMART,
  Provider.TIKTOK,
  Provider.FAIRE,
  Provider.LINNWORKS_TRACKSTAR,
  Provider.LINNWORKS_DIRECT,
  Provider.CIN7_CORE,
  Provider.RITHUM,
];

const bulkUpdate = groupInput({
  shipDate: input<Date>(),
  shippingSelection: input<Carriers | null>(),
  notifyCustomer: input<boolean>(),
  notifyMarketplace: input<boolean>(),
  transferInventory: input<boolean>(),
});

export const defaultBulkUpdate: MarkAsFulfilledFormValue["bulkUpdate"] = {
  shipDate: new Date(),
  shippingSelection: null,
  notifyCustomer: true,
  notifyMarketplace: true,
  transferInventory: false,
};

export function fulfillmentGroupToRowInput(
  group: Pick<
    FulfillmentGroupData,
    "_id" | "customerName" | "notifyMarketplace" | "providers"
  > & { orders: readonly { name: string }[] },
  transferInventory = false,
): FulfillmentModalRowInput {
  const notifyInitiallyEnabled = shouldInitiallyNotifyMarketplace(group);

  return {
    fulfillmentGroupId: group._id,
    orderName: group.orders.map((order) => order.name).join(", "),
    customerName: group.customerName || "",
    shipDate: new Date(),
    shippingSelection: null,
    trackingNumber: "",
    notifyCustomer: notifyInitiallyEnabled,
    notifyMarketplace: notifyInitiallyEnabled,
    transferInventory,
    providers: group.providers ?? [],
  };
}

export function shouldInitiallyNotifyMarketplace(
  group: Pick<FulfillmentGroupData, "notifyMarketplace" | "providers">,
): boolean {
  return (
    group.notifyMarketplace !== false &&
    !requiresTrackingToNotifyMarketplace(group.providers ?? [])
  );
}

export function canNotifyMarketplace(
  row: Pick<
    FulfillmentModalRowInput,
    "providers" | "shippingSelection" | "trackingNumber"
  >,
): boolean {
  return (
    !requiresTrackingToNotifyMarketplace(row.providers) ||
    (!!row.trackingNumber && !!row.shippingSelection)
  );
}

export function applyBulkUpdateToRows<
  Key extends keyof MarkAsFulfilledFormValue["bulkUpdate"],
>(
  rows: readonly FulfillmentModalRowInput[],
  key: Key,
  value: MarkAsFulfilledFormValue["bulkUpdate"][Key],
): FulfillmentModalRowInput[] {
  return rows.map((row) => {
    const updatedRow = { ...row, [key]: value };

    if (
      key === "notifyMarketplace" &&
      value === true &&
      !canNotifyMarketplace(updatedRow)
    ) {
      return { ...updatedRow, notifyCustomer: false, notifyMarketplace: false };
    }

    return updatedRow;
  });
}

function requiresTrackingToNotifyMarketplace(
  providers: readonly Provider[],
): boolean {
  return providers.some((provider) =>
    ProvidersRequiringTrackingForFulfillment.includes(provider),
  );
}

export const rowInput = groupInput({
  fulfillmentGroupId: input<string>(),
  orderName: input<string>(),
  customerName: input<string>(),
  shipDate: input<Date>(),
  shippingSelection: input<Carriers | null>(),
  trackingNumber: input<string>(),
  notifyCustomer: input<boolean>(),
  notifyMarketplace: input<boolean>(),
  transferInventory: input<boolean>(),
  providers: input<Provider[]>(),
});

export type FulfillmentModalRowInput = InputSchema.Value<typeof rowInput>;

export const defaultRowData: FulfillmentModalRowInput = {
  fulfillmentGroupId: "",
  orderName: "",
  customerName: "",
  shipDate: new Date(),
  shippingSelection: null,
  trackingNumber: "",
  notifyCustomer: true,
  notifyMarketplace: true,
  transferInventory: false,
  providers: [],
};

export const markAsFulfilledForm = groupInput({
  defaultValue: input<string>(),
  bulkUpdate,
  rowData: listInput(
    () => rowInput,
    () => defaultRowData,
    (_, index) => index,
  ),
});

export type MarkAsFulfilledFormValue = InputSchema.Value<
  typeof markAsFulfilledForm
>;

export type MarkAsFulfilledForm = InputSchema.Input<typeof markAsFulfilledForm>;
